import React, { useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { userService } from '../_services';
import '../mysqpaymentform.css';

export default function PaymentFormNew({
    paymentForm,
    Fname,
    Lname,
    Address1,
    Address2,
    State,
    City,
    Zip,
    Email,
    toggle_Payment_pop,
    SuccessMessage,
    ProductName,
    quantity,
    group_number,
    base64blob,
    forCard,
    total_price,
}) {
    const cardContainerRef = useRef(null);
    const cardRef          = useRef(null); // holds the Square card instance

    const [loader, setLoader]     = useState(false);
    const [errormsg, setErrormsg] = useState('');

    // ── Square SDK initialisation ─────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        const initSquare = async () => {
            try {
                const apidata = await userService.GetPaymentStuff('');
                if (!apidata.length || cancelled) return;

                if (!window.Square) {
                    throw new Error('Square.js failed to load properly');
                }

                const payments = paymentForm(apidata[0].applicationId, apidata[0].locationId);
                let card;
                try {
                    card = await payments.card();
                    await card.attach(cardContainerRef.current);
                    cardRef.current = card;
                } catch (e) {
                    console.error('Initializing Card failed', e);
                    return;
                }

                const cardButton = document.getElementById('card-button');
                if (cardButton && !cancelled) {
                    cardButton.addEventListener('click', async (event) => {
                        await handlePaymentMethodSubmission(event, card, apidata[0].locationId);
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setErrormsg('Payment Failed! Please try later');
                    setLoader(false);
                }
            }
        };

        initSquare();

        return () => {
            cancelled = true;
            // Destroy the Square card instance on unmount so React StrictMode's
            // double-invoke (mount → unmount → mount) doesn't attach two forms.
            if (cardRef.current) {
                cardRef.current.destroy().catch(() => {});
                cardRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Helpers ───────────────────────────────────────────────────────────
    const uuidv4 = () =>
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });

    const tokenize = async (paymentMethod) => {
        setErrormsg('');
        const tokenResult = await paymentMethod.tokenize();
        if (tokenResult.status === 'OK') {
            return tokenResult.token;
        }
        let errorMessage = `Tokenization failed-status: ${tokenResult.status}`;
        if (tokenResult.errors) {
            errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
        }
        setErrormsg(tokenResult.errors[0].message);
        throw new Error(errorMessage);
    };

    const createPayment = (UserId, idempotency_key, source_id) => {
        setErrormsg('');
        userService
            .CreatePayment(
                UserId, idempotency_key, source_id,
                Fname, Lname, Address1, Address2, State, City, Zip, Email,
                group_number, ProductName, quantity, base64blob, total_price
            )
            .then(async (apidata) => {
                try {
                    if (apidata.length > 0) {
                        if (apidata[0].code === '-1') {
                            const apiMsg = apidata[0].message;
                            let errorMsg = apiMsg;
                            if (apiMsg === 'GENERIC_DECLINE')                  errorMsg = 'Card number declined';
                            else if (apiMsg === 'INVALID_EXPIRATION')          errorMsg = 'Card expiration date incorrect';
                            else if (apiMsg === 'CVV_FAILURE')                 errorMsg = 'Card CVV incorrect';
                            else if (apiMsg === 'ADDRESS_VERIFICATION_FAILURE') errorMsg = 'Card postal code incorrect';
                            setErrormsg(errorMsg);
                        } else {
                            // Payment succeeded — let the parent handle the success view
                            SuccessMessage(true);
                        }
                        setLoader(false);
                    }
                } catch (error) {
                    setErrormsg('An unexpected error occurred. Please try again.');
                    setLoader(false);
                }
            });
    };

    const displayPaymentResults = (status) => {
        const statusContainer = document.getElementById('payment-status-container');
        if (status === 'SUCCESS') {
            statusContainer.classList.remove('is-failure');
            statusContainer.classList.add('is-success');
        } else {
            statusContainer.classList.remove('is-success');
            statusContainer.classList.add('is-failure');
        }
        statusContainer.style.visibility = 'visible';
    };

    const handlePaymentMethodSubmission = async (event, paymentMethod) => {
        event.preventDefault();
        try {
            setLoader(true);
            const token           = await tokenize(paymentMethod);
            const idempotency_key = uuidv4();
            const Usermeta_Data   = window.atob(localStorage.getItem('ajs_user_id'));
            const UserId          = Usermeta_Data.split(':')[0];
            createPayment(UserId, idempotency_key, token);
            await displayPaymentResults('SUCCESS');
        } catch (e) {
            setLoader(false);
            if (e.errors != null) {
                setErrormsg(e.errors[0]['message'].split('.')[0]);
            }
            displayPaymentResults('FAILURE');
        }
    };

    return (
        <div className="container sq-payment-form">
            <link rel="stylesheet" type="text/css" href="mysqpaymentform.css" />
            <div id="form-container">
                <div id="sq-ccbox" className="sqbox">
                    <form id="payment-form">
                        <div id="card-container" ref={cardContainerRef}></div>
                    </form>
                    <div id="payment-status-container"></div>
                </div>

                {errormsg && (
                    <Alert variant="destructive" className="mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{errormsg}</AlertDescription>
                    </Alert>
                )}

                <div className="flex flex-row gap-2 mt-3 justify-end">
                    <Button
                        variant="outline"
                        onClick={toggle_Payment_pop}
                        disabled={loader}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="card-button"
                        className="bg-accent hover:!bg-accent text-white"
                        disabled={loader}
                    >
                        {loader
                            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
                            : `Pay $${forCard ? total_price : '89.95'}`
                        }
                    </Button>
                </div>
            </div>
        </div>
    );
}
